import { Suspense, useEffect, useLayoutEffect, type ReactNode } from 'react'
import { Outlet } from 'react-router-dom'

import { fleetTopic } from '@libs/realtime'

import { getTenantId } from '../../../config/env'
import { useAuth } from '../../../hooks/useAuth'
import { useBranding } from '../../../hooks/useBranding'
import { useLiveChannel } from '../../../hooks/useLiveChannel'
import { ToastQueueProvider } from '../../../hooks/useToastQueue'
import { applyBrandVars } from '../../../lib/branding-shades'
import { Spinner } from '../../atoms'
import { LicenseExpirationBanner } from '../../molecules/LicenseExpirationBanner'
import { AssistantSurface } from '../../organisms/Assistant/AssistantSurface'
// CCTV element uses ezuikit-js, which throws "abort fetch" / AbortError on
// destroy() under React-19 StrictMode and tab navigation. Side-effect import
// installs a global swallower once at boot — the component itself renders
// null. Mounted here (not in the CCTV element) so a single install survives
// element mount/unmount cycles.
import { GlobalErrorSwallow } from '../../organisms/DashboardElements/cctv/GlobalErrorSwallow'
import { InsufficientCreditsModal } from '../../organisms/InsufficientCreditsModal/InsufficientCreditsModal'
import { SosCallOverlay } from '../../organisms/SosCall/SosCallOverlay'
import { AppHeader } from './AppHeader'
import { GamepadConnectionToasts } from './GamepadConnectionToasts'

function PageLoadingFallback() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Spinner size="lg" label="Loading..." />
    </div>
  )
}

export function AppLayout({ children }: { children?: ReactNode } = {}) {
  const { user, authState, checkAuth } = useAuth()

  // `authState` leaves 'loading' only when something verifies the session, and normally that is
  // AuthGuard. The dashboard view is the exception: it sits OUTSIDE AuthGuard (it also serves
  // public dashboards) and renders this layout itself for a signed-in user, so a direct load of a
  // dashboard URL left `authState` at 'loading' forever. Everything keyed on a verified identity —
  // the sitewide assistant resuming its conversation, first of all — then silently never ran. Under
  // AuthGuard this is a no-op: the layout only mounts once the session is already verified.
  useEffect(() => {
    if (authState === 'loading') void checkAuth?.()
  }, [authState, checkAuth])
  const { primaryColor, secondaryColor, logoUrl } = useBranding()

  // FIX 2 — the sitewide assistant is gated on the `ai` module, exactly like
  // the backend gates its three endpoints (`requireModule(ctx, ModuleLabel.AI)`
  // on chat.ts/chat-stream.ts/sessions/active.ts). Mirrors
  // `pages/settings/settings-nav.ts` + `SettingsSidebar.tsx`'s `isCentral`
  // bypass verbatim: a central operator (no `tenantId`) implicitly has every
  // module, matching the backend's `requireModule` bypass when
  // `ctx.isCentral` is true — central operators must never be locked out.
  // Without this gate, a tenant lacking `ai` got a prominent floating launcher
  // on every route whose mount GET 403s into the provider's swallow and whose
  // first send renders a raw `Error: …`.
  const isCentral = !user?.tenantId
  const hasAiModule = isCentral || (user?.assignedModules ?? []).includes('ai')
  // Task 3 — the per-tenant sitewide-assistant off-switch. An ADDITIONAL AND
  // condition alongside `hasAiModule`, never a replacement for it — the
  // central bypass above must stay intact. Frontend gating is navigation
  // hygiene, not security; `ChatPipeline` enforces this server-side for real.
  // Defaults to enabled (`true`) when absent, same as every other optional
  // `/users/me` field, so a stale cached user object never wrongly hides it.
  const assistantEnabled = user?.assistantEnabled ?? true

  // Robot fleet realtime channel — feeds the open-alert store (read by the
  // alert inbox). Self-gating: useLiveChannel stays disconnected when
  // getLiveChannelUrl() is null (on-prem / no broker configured). No bridge
  // provider here, so bridge emits are skipped; the store feed still runs.
  useLiveChannel({ topics: [fleetTopic(getTenantId())] })

  // Hoist the brand CSS variables onto <html> so that <body>'s background
  // (which paints the macOS overscroll-bounce area beneath <main>) can read
  // them via `rgb(var(--brand-primary-950))`. Inline style on the AppLayout
  // <div> would only cascade DOWN into the tree, not up to body.
  useLayoutEffect(() => {
    applyBrandVars(primaryColor, secondaryColor)
  }, [primaryColor, secondaryColor])

  return (
    <ToastQueueProvider>
      <div className="flex h-dvh flex-col">
        <GlobalErrorSwallow />
        <InsufficientCreditsModal />
        {/* Renders nothing; turns a controller connect/disconnect into a toast. Must stay INSIDE
            ToastQueueProvider — outside it `useToast()` is a silent no-op and this dies quietly. */}
        <GamepadConnectionToasts />
        {/* The SOS emergency modal is deliberately NOT here. It mounts on the dashboard pages only
            (DashboardSosEmergency), and pops only for a device the open dashboard shows — the
            product rule is that no other console route ever interrupts. The live channel above
            still feeds the alert store console-wide, so the bell and inbox stay live everywhere. */}
        {/* The CALL that follows Accept, however, IS here — and that difference is the point. The
            modal asks a question and belongs to the dashboard that asked it; the call is a
            conversation with a person in trouble, and an operator on one needs to reach the map,
            the roster and the cameras without hanging up on them. */}
        <SosCallOverlay />
        <AppHeader logoUrl={logoUrl} />
        <LicenseExpirationBanner expirationDate={user?.licenseExpiresAt ?? undefined} />
        {/* The page and the docked assistant, side by side. A docked panel is an in-flow
            column here, so it takes room from the page rather than covering it — the page
            reflows narrower and both stay usable. `min-w-0` lets <main> shrink below its
            content's width; `min-h-0` keeps <main>'s own overflow scrolling. */}
        <div className="flex min-h-0 flex-1">
          <main className="flex min-w-0 flex-1 flex-col overflow-auto bg-gray-50 dark:bg-gray-900">
            <Suspense fallback={<PageLoadingFallback />}>{children ?? <Outlet />}</Suspense>
          </main>
          {/* The sitewide assistant's visible half. Self-gates to just its launcher button
              while closed (no <AssistantProvider> is required for that shell); see
              AssistantSurface.tsx's header comment. It renders HERE in every form — the
              launcher and the floating window are `fixed`, so only the dock uses the slot.
              Coming after the header and <main> in the DOM, it would win every z-index tie
              with their overlays (the header's drawers, the page's dialogs); its layer is
              chosen so there is no tie, see assistant-layer.ts. Gated on the `ai` module
              (see `hasAiModule` above) — a tenant without it never sees the launcher,
              matching the backend's requireModule gate on every endpoint this surface
              drives. ALSO gated on `assistantEnabled` (Task 3's operator/tenant off-switch)
              — an additional AND, not a replacement for the module gate. */}
          {hasAiModule && assistantEnabled && <AssistantSurface />}
        </div>
      </div>
    </ToastQueueProvider>
  )
}
